/** @jsxImportSource theme-ui */
import { jsx } from "theme-ui";
import React, { useState } from "react";
import { Flex, Box, Link, Image, Text } from "@theme-ui/components";
import { useResponsiveValue } from "@theme-ui/match-media";
import { navigate, Link as InternalLink } from "gatsby";
import { useSelector, useDispatch } from "react-redux";
import { StaticImage } from "gatsby-plugin-image";
import moment from "moment";
import footer from "../data/footer.yml";
import meFooter from "../data/me_footer.yml";
import { setPageRegion } from "../state/app";
import { SUPPORTED_REGIONS } from "../utils/region";
import { colors, typography } from "../styles/theme";
import { Accordion, SocialHandles } from "./organisms";
import { CustomLink, Dropdown, RegionalLink } from "./atoms";
import greythrLogo from "../../static/logos/greythr-logo.svg";
import whatsAppLogo from "../../static/logos/whatsapp-logo.svg";

const FooterLink = (props) => {
	const { path, title, region, regional = false, customStyles } = props;
	const sx = {
		...typography.textMd400,
		color: colors.neutral900,
		"&:hover": {
			textDecoration: ["none", "none", "underline"],
			color: colors.primaryMain,
		},
		...customStyles,
	};
	return path.split("/")[0] === "https:" ? (
		<Link
			sx={sx}
			href={path}
			target="_blank"
			rel="noopener"
		>
			<Text as="span">{title}</Text>
		</Link>
	) : (
		<RegionalLink
			className="flink"
			sx={sx}
			to={path}
			region={region}
			regional={regional}
		>
			<Text as="span">{title}</Text>
		</RegionalLink>
	);
};

const RegionSelector = () => {
	const dispatch = useDispatch();
	const options = SUPPORTED_REGIONS.reduce((acc, curr) => {
		acc.push({ label: curr.name, value: curr.code });
		return acc;
	}, []);
	const region = useSelector((state) => state.app.region);
	const [selectedRegion, setSelectedRegion] = useState(
		options.filter((r) => r.label === region || r.value === region)
	);

	const onChange = (option) => {
		setSelectedRegion(option);
		dispatch(setPageRegion(option.value));
		navigate("/");
	};

	return (
		<Dropdown
			options={options}
			onChange={onChange}
			value={selectedRegion}
			menuPlacement="auto"
			customStyles={{ width: "10.063rem" }}
			aria-label="region-selector"
		/>
	);
};

const Certifications = ({ region }) => {
	const blogLink =
		region === "ME"
			? "/middle-east/blog/greytip-software-is-now-iso-certified/"
			: "/blog/greytip-software-is-now-iso-certified/";
	const whatsAppLink = "https://wa.me/919606021119";
	return (
		<Flex
			sx={styles.certifications}
		>
			<Image
				src={greythrLogo}
				alt="greytHR-logo"
				sx={{ width: ["6.438rem", "6.438rem", "11.25rem"] }}
			/>
			<Flex sx={{
				gap: [3, 3, "1.5rem"],
				height: "100%",
			}}>
				<StaticImage
					src="../../static/images/new-gdpr.png"
					alt="GDPR Compliant certification badge"
					style={styles.circleLogos}
				/>
				<StaticImage
					src="../../static/images/soc2.png"
					alt="Soc2 certification badge"
					style={styles.circleLogos}
				/>
				<InternalLink to={blogLink}>
					<StaticImage
						src="../../static/images/iso.png"
						alt="ISO Certification Badge"
						style={styles.iso}
					/>
				</InternalLink>
			</Flex>
			{region === "IN" && <Link
				href={whatsAppLink}
				aria-label="Message us on WhatsApp"
				target="_blank"
				rel="noopener"
				sx={styles.whatsapp}>
				<Flex sx={{ gap: "0.75rem", alignItems: "center", p: "0.75rem", minWidth: "16.75rem"}}>
					<Image src={whatsAppLogo} alt="WhatsApp Logo" style={{ width: "2.5rem" }} />
					<Text>Message us on WhatsApp</Text>
				</Flex>
			</Link>}
		</Flex>
	);
};

const AppLinks = () => {
	return (
		<Flex sx={styles.appLinks}>
			<CustomLink url="https://bit.ly/greytHRapp">
				<StaticImage
					src="../../static/images/play-store.png"
					alt="PlayStore"
					sx={styles.storeLinks}
				/>
			</CustomLink>
			<CustomLink url="https://cutt.ly/greytHRapp">
				<StaticImage
					src="../../static/images/app-store.png"
					alt="AppStore"
					sx={styles.storeLinks}
				/>
			</CustomLink>
		</Flex>
	);
};

const DesktopFooterItems = ({ data, region }) => {
	
  return (
    <Flex sx={styles.footerItems}>
      {Object.entries(data).map(([key, value]) => {
        const { name, items } = value;
        if (name === "Competitors") return;
        return (
          <>
            <ul key={key} sx={styles.itemList}>
              <li sx={{ ...typography.h5, mb: "1.438rem" }}>{name}</li>
              {items.map(({ title, path, regional }) => (
                <li key={title}>
                  <FooterLink
                    path={path}
                    region={region}
                    regional={regional}
                    title={title}
                  />
                </li>
              ))}
              {name === "greytHR Customers" ? (
                <li key="app-links">
                  <AppLinks />
                </li>
              ) : null}
              {(name === "Product" && (region === "IN" || region === "ME")) && (
                <ul sx={{ ...styles.itemList, mt: "2rem", gap: "0rem" }}>
                  <li sx={{ ...typography.h5, mb: "1.438rem" }}>Competitors</li>
                  <li>
                    <FooterLink
                      path="/compare/keka/"
                      region={region}
                      regional={true}
                      title="greytHR vs Keka"
                    />
                  </li>
                </ul>
              )}
            </ul>
          </>
        );
      })}
    </Flex>
  );
};

const MobileFooterItems = ({ data, region }) => {
	return (
		<Box>
			{data &&
				Object.entries(data).map(([key, value]) => {
					const { name, items } = value;
					return (
						<Box
							key={key}
							sx={styles.accordion}
						>
							<Accordion>
								<div
									style={{ px: 0, my: 0, pb: 0 }}
									label={name}
									openIconName="ChevronRight"
									closeIconName="ChevronRight"
									iconSize={20}
									showChevronIcon
								>
									{items.map(({ title, path, regional }) => title !== "Contact us" && (
										<div sx={styles.mobileFooterItems} key={title}>
											<FooterLink
												path={path}
												region={region}
												regional={regional}
												title={title}
											/>
										</div>
									))}
								</div>
							</Accordion>
						</Box>
					);
				})}
			{!(region === "ID" || region === "SG" || region === "MY") && (
				<Box p={3} mt={3}>
					<FooterLink
						path={"/contact-us/"}
						region={region}
						regional={true}
						title={"Contact us"}
						customStyles={{
							...typography.textMd500,
						}}
					/>
				</Box>
			)}
			<AppLinks />
		</Box>
	);
};

const Footer = ({ region }) => {
	const currentYear = moment().year();
	var footerData = footer;
	var pageRegionCode = region;
	if (!region) {
		//****Getting Region from date when it is not passed.*/
		pageRegionCode = useSelector((state) => state.app.region);
	}
	const footerMapping = {
		IN: footer,
		ME: meFooter,
	};
	footerData = footerMapping[pageRegionCode] || footer;
	
	const footerItems = useResponsiveValue([
		<MobileFooterItems
			data={footerData}
			region={pageRegionCode}
		/>,
		<MobileFooterItems
			data={footerData}
			region={pageRegionCode}
		/>,
		<DesktopFooterItems
			data={footerData}
			region={pageRegionCode}
		/>
	],{
    defaultIndex: 2,
  });

	return (
		<footer sx={styles.container}>
			{!["ID", "SG", "MY"].includes(pageRegionCode) && (
				footerItems
			)}
			<Certifications region={pageRegionCode}/>
			<Flex sx={styles.lastRow}>
				<Flex sx={styles.bottomLinks}>
					<Box>© {currentYear} Greytip Software Pvt. Ltd.</Box>
					<InternalLink to="/privacy-policy/">Privacy Policy</InternalLink>
					<InternalLink to="/terms-service/">Terms of Use</InternalLink>
					{/* Below link will be activated once the respective page gets created, commenting for now. */}
					{/* <InternalLink to="">Security Controls</InternalLink> */}
				</Flex>
				<Box sx={{ 
					order: 2,
					"@media(max-width: 1077px)": {
						order: 1,
					},
				}}>
					<RegionSelector />
				</Box>
				<Box
					sx={{
						order: 3,
						"@media(max-width: 1077px)": {
							order: 2,
						},
					}}
				>
					<SocialHandles region={pageRegionCode} />
				</Box>
			</Flex>
		</footer>
	);
};

const styles = {
	container: {
		bg: colors.white,
		py: [5, 5, "7rem"],
		px: [3, 3, "3rem", 0],
		width: ["100%", "100%", "100%", "1346px"],
		mx: "auto",
		"@media(max-width: 1420px)": {
			width: "90%",
		},
		"@media(max-width: 1023px)": {
			width: "100%",
			px: [3, 3, "3rem"],
		},
	},
	circleLogos: {
		width: "3.438rem",
		height: "3.438rem",
		"@media(maxWidth: 599px)": {
			width: "3rem",
			height: "3rem",
		},
	},
	iso: {
		width: "6.75rem",
		height: "3.438rem",
		"@media(maxWidth: 599px)": {
			width: "5.875rem",
			height: "3rem",
		},
	},
	bottomLinks: {
		gap: "1.5rem",
		alignItems: "center",
		...typography.textSm,
		order: [3, 3, 3, 2],
		color: colors.neutral900,
		flexWrap: ["wrap", "wrap", "no-wrap"],
		justifyContent: ["center", "center", "center", "flex-start"],
		"@media(max-width: 1077px)": {
			justifyContent: "center",
			order: 3,
		},
		a: {
			color: colors.neutral900,
			textDecoration: "none",
			"&:hover": {
				color: colors.primaryMain,
				textDecoration: "underline"
			}
		}
	},
	lastRow: {
		justifyContent: ["center", "center", "center", "space-between"],
		alignItems: "center",
		borderTop: `1px solid ${colors.neutral200}`,
		pt: [4, 4, "1.5rem", "1.25rem"],
		gap: [4, 4, "1.5rem"],
		flexDirection: ["column", "column", "column", "row"],
		"@media(max-width: 1077px)": {
			justifyContent: "center",
			flexDirection: "column",
			pt: [4, 4, "1.5rem"],
		}
	},
	footerItems: {
		flexWrap: "nowrap",
		justifyContent: "center",
		gap: 5,
		"@media(max-width: 1023px)": {
			flexWrap: "wrap",
			justifyContent: "flex-start",
		},
	},
	itemList: {
		m: 0,
		p: 0,
		listStyleType: "none",
		display: "flex",
		flexDirection: "column",
		gap: "0.813rem",
	},
	accordion: {
		".accordion-label": {
			...typography.textMd500,
			mr: 3,
		},
		".accordion-heading": {
			p: 3,
		},
		".accordion-heading.close": {
			".accordion-label": {
				color: colors.black,
			},
			"svg": {
				stroke: colors.black
			}
		},
		".accordion-heading.open": {
			".accordion-label": {
				color: colors.primaryMain,
			},
			"svg": {
				stroke: colors.primaryMain
			}
		},
		".accordion-section": {
			py: 3,
			borderBottom: `1px solid ${colors.neutral200}`
		},
	},
	appLinks: {
		mt: [4, 4, 0],
		gap: "1.5rem",
		maxWidth: ["100%", "100%", "11.938rem"],
		flexDirection: ["row", "row", "column"],
		justifyContent: ["center", "center", "flex-start"],
		alignItems: ["center", "center", "flex-start"],
	},
	mobileFooterItems: {
		p: 3,
		...typography.textMd400,
		color: colors.neutral900,
		"&:hover": {
			bg: [colors.primary100, colors.primary100, "transparent" ],
		}
	},
	certifications: {
		justifyContent: ["center", "center", "center", "flex-start"],
		alignItems: "center",
		flexWrap: ["wrap", "wrap", "wrap", "nowrap"],
		gap: [4, 4, "3rem", 5],
		py: [4, 4, "3rem", "1.5rem"],
		"@media(max-width: 1077px)": {
			justifyContent: "center",
			gap: [4, 4, "3rem"],
			py: [4, 4, "3rem"],
		}
	},
	storeLinks: {
		width: "11.938rem",
		"@media(max-width: 599px)": {
			width: "9.563rem",
		}
	},
	whatsapp: {
		...typography.textMd400,
		color: colors.neutral900,
		"&:hover": {
			textDecoration: "underline",
			color: colors.primaryMain
		},
		ml: [0, 0, 0, "auto"]
	}
};

export default Footer;
